import { Divider } from "@amzn/storm-ui";
import { useMountEffect } from "@react-hookz/web";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useUnmount } from "react-use";
import styled from "styled-components";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../constants/telemetry";
import { dimensionHydrationStore } from "../../store/dimension.hydration";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../store/dimension.status";
import { dimensionValueStore } from "../../store/dimension.value";
import type {
    DimensionMetadata,
    DimensionTemplates,
} from "../../types/dimensions";
import { logWithTakt } from "../../utils/telemetry.utils";

import { DimensionGroup } from "./DimensionGroup";
import * as DimensionTemplateBuilderHelpers from "./DimensionTemplateBuilderHelpers";
import { HydrationPrompt } from "./common/hydration.components";

const HideableDiv = styled.div`
    display: ${(props: { hide: boolean }) => (props.hide ? "none" : "block")};
`;

export interface DimensionTemplateBuilderProps {
    dimensionSections?: DimensionTemplates[][];
    unusedDimensionIds?: string[];
    skipHasAdvertiser?: boolean;
    enableDimensionsDebug?: boolean;
    dimensionMetadata?: DimensionMetadata;
    hydrationPayload?: object | null;
}

export const DimensionTemplateBuilder: React.FunctionComponent<
    DimensionTemplateBuilderProps
> = (props) => {
    const {
        dimensionSections = [],
        unusedDimensionIds = [],
        skipHasAdvertiser,
        enableDimensionsDebug,
        dimensionMetadata,
        hydrationPayload,
    } = props;
    const usedDimensionSections =
        DimensionTemplateBuilderHelpers.getUsedDimensionSections(
            dimensionSections,
            unusedDimensionIds,
        );
    const dimensionIds = DimensionTemplateBuilderHelpers.getDimensionIds(
        usedDimensionSections,
    );
    const hasAdvertiser =
        !skipHasAdvertiser &&
        DimensionTemplateBuilderHelpers.hasAdvertiser(usedDimensionSections);
    const [initDimensionsData, clearAllValues] = useStore(
        dimensionValueStore,
        (state) => [state.initDimensions, state.clearAllValues],
    );
    const [initDimensionsStatus, clearStatus] = useStore(
        dimensionStatusStore,
        (state) => [state.initDimensions, state.clearStatus],
    );

    const { domainId, templateId } = useParams();

    const [
        initHydrationforRequest,
        clearHydration,
        hydrationErrorMessages,
        isHydrationDone,
        currentDimensions,
        setCurrentDimensions,
    ] = useStore(dimensionHydrationStore, (state) => [
        state.initHydration,
        state.clearAllState,
        state.errorMessages,
        state.isHydrationDone(),
        state.currentDimensions,
        state.setCurrentDimensions,
    ]);

    const requiresHydration = React.useMemo(
        () => hydrationPayload && Object.keys(hydrationPayload).length > 0,
        [hydrationPayload],
    );

    useMountEffect(() => {
        initDimensionsData(dimensionIds);
        setCurrentDimensions(dimensionSections.flat());
        if (requiresHydration) {
            initHydrationforRequest(dimensionIds, hydrationPayload);
            initDimensionsStatus(
                dimensionIds,
                DimensionStatus.PENDING_HYDRATION,
            );
        } else {
            initDimensionsStatus(dimensionIds);
        }
    });
    useUnmount(() => {
        clearAllValues();
        clearStatus();
        clearHydration();
    });

    const hydrationErrorMessagesLabels = React.useMemo(() => {
        return Object.entries(hydrationErrorMessages).map(
            ([dimension, cause]) =>
                [
                    dimension,
                    currentDimensions[dimension]?.configuration.label ??
                        dimension,
                    cause,
                    currentDimensions[dimension].type,
                ] as [string, string, string, string],
        );
    }, [hydrationErrorMessages]);

    useEffect(() => {
        if (isHydrationDone) {
            if (hydrationErrorMessagesLabels.length > 0) {
                logWithTakt(
                    TELEMETRY_ID.IHUB_CUSTOM_INSIGHTS_HYDRATION_WARNING,
                    {
                        domainIdentifer: domainId,
                        templateIdentifier: templateId,
                    },
                );
                for (const [
                    dimensionId,
                    dimensionLabel,
                    cause,
                    dimensionType,
                ] of hydrationErrorMessagesLabels) {
                    logWithTakt(
                        TELEMETRY_ID.IHUB_CUSTOM_INSIGHTS_HYDRATION_DIMENSION_WARNING,
                        {
                            domainIdentifer: domainId,
                            templateIdentifier: templateId,
                            dimensionId,
                            dimensionLabel,
                            cause,
                            dimensionType,
                        },
                    );
                }
            } else {
                logWithTakt(
                    TELEMETRY_ID.IHUB_CUSTOM_INSIGHTS_HYDRATION_SUCCESS,
                    {
                        domainIdentifer: domainId,
                        templateIdentifier: templateId,
                    },
                );
            }
        }
    }, [isHydrationDone]);

    return (
        <div>
            <HydrationPrompt
                requiresHydration={Boolean(requiresHydration)}
                hydrationErrorMessages={hydrationErrorMessagesLabels}
                isHydrationDone={isHydrationDone}
            />
            <HideableDiv hide={!isHydrationDone}>
                {usedDimensionSections.map((dimensions, index) => (
                    <div key={index}>
                        <DimensionGroup
                            templates={dimensions}
                            hasAdvertiser={hasAdvertiser}
                            enableDebugMode={enableDimensionsDebug}
                            dimensionMetadata={dimensionMetadata}
                        />
                        {index < usedDimensionSections.length - 1 && (
                            <Divider />
                        )}
                    </div>
                ))}
            </HideableDiv>
        </div>
    );
};
